import { DragEvent } from '@interactjs/types';
import { createPopper, Options as PopperOptions } from '@popperjs/core';
import interact from 'interactjs';
import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { CreateContact } from 'smg/customer-api/contact';
import * as models from 'smg/customer-api/models';
import { Session } from 'smg/customer-api/session';
import { OpenQuotes, QuoteRefQuery, RevisionRequestsByProof } from 'smg/customer-api/v2/quotes';
import { CreateRevisionRequests, DeleteRevisionRequest, UpdateRevisionRequest } from 'smg/customer-api/v2/revision-requests';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import { Phone } from 'smg/phone';
import { Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { Uploader } from 'smg/uploader/v2';
import * as uploads from 'smg/uploads';
import { RemToPx } from 'smg/util';
import { Relref } from '../hugo';
import { PromptProofForRevision } from '../quotes';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

const chatBubbleSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';

// Snapshot upload blob data into IUserEventUpload[] for optimistic UI rendering
const captureUploads = (uploader: Uploader): models.IUserEventUpload[] =>
    uploader.uploads().map(u => ({
        upload: {
            key: u.data ? URL.createObjectURL(u.data) : '',
            name: u.name,
            mime: u.mime,
        },
    }));

void main();

async function main() {
    const sess = Session().catch(() => undefined);

    const hasProofsContainer = document.querySelector('#has-proofs-container') as HTMLElement;
    const noProofsContainer = document.querySelector('#no-proofs-container') as HTMLElement;
    const spinner = document.querySelector('#body-spinner') as HTMLElement;
    const $notFound = document.querySelector('#not-found-container') as HTMLElement;
    const $notFoundHint = document.querySelector('#not-found-hint') as HTMLElement;

    const $breadcrumb = document.querySelector('#rev-breadcrumb') as HTMLElement;
    const $breadcrumbSerial = document.querySelector('#rev-breadcrumb-serial') as HTMLElement;
    const $breadcrumbQuoteLink = document.querySelector('#rev-breadcrumb-quote-link') as HTMLAnchorElement;
    const $breadcrumbProof = document.querySelector('#rev-breadcrumb-proof') as HTMLElement;

    const showNotFound = (hint: string, serial?: string) => {
        spinner.parentElement?.removeChild(spinner);
        $notFoundHint.textContent = hint;
        $notFound.classList.remove('d-none');

        // Show breadcrumb with whatever info we have
        if (serial) {
            $breadcrumbSerial.textContent = serial;
            $breadcrumbQuoteLink.href = `${Relref('/quote')}?id=${quoteHash || ''}`;
        } else {
            // Remove the quote breadcrumb item — just show "Quotes / Proof not found"
            $breadcrumbQuoteLink.parentElement?.remove();
        }
        $breadcrumbProof.textContent = 'Proof not found';
        $breadcrumb.classList.remove('d-none');
    };

    const quoteHash = QueryString().get('id');
    if (!quoteHash) {
        showNotFound('This link is incomplete and does not include your quote. Open the full link from the email again.');
        return;
    }

    let quote: models.HyejooQuote | undefined;
    try {
        const data = await OpenQuotes({
            q: QuoteRefQuery(quoteHash),
            data: 'serial,quoteProofs,revisionRequests,key',
            max: 1,
        });
        quote = data.results[0];
        if (quote) {
            const byProof = RevisionRequestsByProof(quote.revisionRequests);
            for (const proof of quote.quoteProofs) {
                proof.userEvents = byProof.get(proof.id || '') ?? [];
            }
        }
    } catch {
        quote = undefined;
    }
    if (!quote) {
        showNotFound('We cannot open the quote named in this link. Reply to the quote email or contact our team, and we will send a new link.');
        return;
    }
    const quoteKey = quote.key;

    const { serial, quoteProofs } = quote;
    const cloudFront = window.cloudFront;

    const $commentTemplate = document.querySelector('#comment-template') as HTMLTemplateElement;
    const $commentThumbTemplate = document.querySelector('#comment-thumbnail-template') as HTMLTemplateElement;
    const $spotCommentTemplate = document.querySelector('#spot-comment-template') as HTMLTemplateElement;
    const $spotCommentHelpTemplate = document.querySelector('#spot-comment-help-template') as HTMLTemplateElement;

    const showSpotCommentHelp = () => {
        new Modal({
            body: template.Clone($spotCommentHelpTemplate).firstElementChild as HTMLElement,
            confirmText: 'Roger That',
            cancelButton: false,
            close: false,
            appendBodyClass: 'p-0',
            width: '540px',
            onSubmit: () => {
                window.localStorage.setItem('dod-spot-comment-help-shown', 'true');
            },
            appendClassBySelector: {
                '.modal-header': 'd-none',
            },
        });
    };

    const container = document.querySelector('#proofs') as HTMLElement;
    if (quoteProofs.length) {
        // If multiple proofs and no ?proof= param, prompt user to select one
        const quoteProofID = QueryString().get('proof');
        let proof: models.QuoteProof | undefined;

        if (quoteProofID) {
            proof = quoteProofs.find(qp => qp.id === quoteProofID);
        }

        if (!proof) {
            if (quoteProofs.length === 1) {
                proof = quoteProofs[0];
            } else {
                // Show proof selector modal — use selected proof directly from memory
                const selectedID = await PromptProofForRevision(quoteProofs, cloudFront);
                if (!selectedID) {
                    spinner.parentElement?.removeChild(spinner);
                    return;
                }
                proof = quoteProofs.find(qp => qp.id === selectedID);
                if (!proof) {
                    spinner.parentElement?.removeChild(spinner);
                    return;
                }
            }
        }

        // Update URL with proof ID so the page is linkable/refreshable
        if (!quoteProofID || quoteProofID !== proof.id) {
            const url = new URL(window.location.href);
            url.searchParams.set('proof', proof.id ?? '');
            history.replaceState(null, '', url.toString());
        }

        // Populate breadcrumb
        $breadcrumbSerial.textContent = serial;
        $breadcrumbQuoteLink.href = `${Relref('/quote')}?id=${quoteHash}`;
        $breadcrumbProof.textContent = `Proof ${proof.number || ''}`;
        $breadcrumb.classList.remove('d-none');

        hasProofsContainer.classList.remove('d-none');

        if (window.localStorage.getItem('dod-spot-comment-help-shown') !== 'true') {
            showSpotCommentHelp();
        }

        {
            const row = html.FromString(`
        <div id="${html.Escape(proof.id || '')}" class="rev-proof">
            <div class="rev-header">
                <div class="d-flex align-items-center flex-wrap gap-2">
                    <h2 class="rev-title">Proof <span class="rev-title-number">#${html.Escape(proof.number || '')}</span></h2>
                    <span class="badge badge-status-review">Awaiting Your Review</span>
                </div>
                <a href="#" class="rev-help-link">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    How does this work?
                </a>
            </div>
            <div class="rev-grid">
                <div class="card rev-viewer">
                    <div class="card-body p-0 proof-image-wrapper">
                        <div class="proof-image-positioner position-relative mx-auto" style="width:fit-content">
                            <img src="${html.Escape(`${cloudFront}/${proof.proof?.file || ''}`)}" class="proof-image img-fluid">
                        </div>
                    </div>
                    <div class="rev-toolbar">
                        <span>Proof ${html.Escape(proof.number || '')} of ${quoteProofs.length}</span>
                        <span>${html.Escape(proof.name || '')}</span>
                    </div>
                </div>
                <div class="rev-sidebar">
                    <div class="rev-section">
                        <div class="rev-section-label text-primary">General Comments</div>
                        <div class="rev-comment-box">
                            <div class="rev-instruction">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                Notes that apply to the whole proof — not a specific spot
                            </div>
                            <form>
                                <textarea class="form-control revision-notes" rows="4" placeholder="Any other comments or questions?"></textarea>
                                <div class="rev-shortcut-hint">
                                    <kbd class="meta-key-ctrl">Ctrl</kbd><kbd class="meta-key-cmd">\u2318</kbd>+<kbd>Enter</kbd> to submit
                                </div>
                                <div class="uploads d-flex flex-wrap gap-2 mt-3"></div>
                                <div class="d-flex justify-content-end gap-2 mt-3">
                                    <label class="btn btn-outline-primary btn-sm mb-0 upload-button">
                                        Upload Files
                                        <input type="file" hidden multiple>
                                    </label>
                                    <button class="btn btn-primary btn-sm submit-button">Submit</button>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="rev-section">
                        <div class="d-flex align-items-center gap-2 mb-2">
                            <div class="rev-section-label mb-0 rev-section-label--secondary">Revision Comments</div>
                            <span class="badge rounded-pill badge-count comment-count">0</span>
                        </div>
                        <div class="rev-comments user-events"></div>
                    </div>
                </div>
            </div>
        </div>`);

            container.appendChild(row);

            const p = document.querySelector(`#${proof.id || ''}`) as HTMLElement;
            const proofImgWrapper = p.querySelector('.proof-image-wrapper') as HTMLElement;
            const proofImg = proofImgWrapper.querySelector('.proof-image') as HTMLImageElement;
            const proofImgPositioner = proofImgWrapper.querySelector('.proof-image-positioner') as HTMLElement;

            const $comments = p.querySelector('.user-events') as HTMLElement;
            const $commentCountEl = p.querySelector('.comment-count') as HTMLElement;

            const $helpLink = p.querySelector('.rev-help-link') as HTMLAnchorElement;
            $helpLink.addEventListener('click', e => {
                e.preventDefault();
                showSpotCommentHelp();
            });

            const form = p.querySelector('.rev-comment-box form') as HTMLFormElement;
            const thumbnails = form.querySelector('.uploads') as HTMLElement;
            const uploadBtn = form.querySelector('.upload-button') as HTMLLabelElement;
            const submit = form.querySelector('.submit-button') as HTMLButtonElement;
            const uploadInput = uploadBtn.querySelector('input[type="file"]') as HTMLInputElement;

            const v2Uploader = new Uploader({
                $input: uploadInput,
                $thumbnailsParent: thumbnails,
                $submitButtons: [submit],
            });

            let commentCount = 0;

            // Derive next spot number from the highest existing number on the proof
            const nextSpotNumber = () => {
                let max = 0;
                for (const el of proofImgWrapper.querySelectorAll<HTMLElement>('.spot-comment:not(.d-none) .spot-comment-icon')) {
                    const n = parseInt(el.textContent || '0', 10);
                    if (n > max) {
                        max = n;
                    }
                }
                return max + 1;
            };

            // Coordinate helpers
            const imgPointToPct = (x: number, y: number) => ({
                x: (x / proofImg.naturalWidth) * 100,
                y: (y / proofImg.naturalHeight) * 100,
            });

            const pctToImgPoint = (x: number, y: number) => ({
                x: (x / 100) * proofImg.naturalWidth,
                y: (y / 100) * proofImg.naturalHeight,
            });

            // Shared Popper config for spot comment popups
            const popperConfig: Partial<PopperOptions> = {
                placement: 'right-start',
                strategy: 'absolute',
                modifiers: [
                    { name: 'offset', options: { offset: [0, RemToPx(.5)] } },
                    { name: 'preventOverflow', options: { boundary: proofImgWrapper } },
                    { name: 'flip', options: { fallbackPlacements: ['bottom', 'left', 'top'] } },
                ],
            };

            // Render user event content into a target element
            const renderContent = ($target: HTMLElement, userEvent: models.UserEvent) => {
                if (!userEvent.content.trim()) {
                    $target.textContent = 'Attachment only';
                    $target.classList.add('fst-italic');
                } else if (userEvent.contentType === 'text/html') {
                    $target.innerHTML = userEvent.content;
                } else {
                    $target.innerHTML = html.Nl2br(html.Escape(userEvent.content));
                }
            };

            // Render user event upload thumbnails into a container
            const renderThumbnails = ($container: HTMLElement, userEvent: models.UserEvent) => {
                for (const upload of userEvent.userEventUploads) {
                    if (upload.upload) {
                        const $thumb = template.Clone($commentThumbTemplate).firstElementChild as HTMLAnchorElement;
                        const $img = $thumb.querySelector('img') as HTMLImageElement;

                        const key = upload.upload.key;
                        const isBlob = key.startsWith('blob:');
                        const url = isBlob ? key : `${cloudFront}/${key}`;

                        $thumb.href = url;
                        $thumb.setAttribute('data-caption', upload.upload.name);
                        $thumb.setAttribute('data-fancybox', userEvent.id);

                        void Picture($img, url, {
                            displayWidth: 100,
                            alt: upload.upload.name,
                            title: upload.upload.name,
                            fancyboxThumb: true,
                            mime: upload.upload.mime,
                        });

                        $container.appendChild($thumb);
                    }
                }
            };

            // Add a comment card to the sidebar
            const prependComment = (userEvent: models.UserEvent) => {
                if (!userEvent.content.length && !userEvent.userEventUploads.length) {
                    return;
                }

                commentCount++;
                $commentCountEl.textContent = String(commentCount);

                const $card = template.Clone($commentTemplate).firstElementChild as HTMLElement;
                $card.dataset.eventId = userEvent.id;

                const $badge = $card.querySelector('.rev-card-badge') as HTMLElement;
                const $type = $card.querySelector('.rev-card-type') as HTMLElement;
                const $time = $card.querySelector('.rev-card-time') as HTMLElement;
                const $content = $card.querySelector('.revision-request-content') as HTMLElement;
                const $images = $card.querySelector('.revision-request-images') as HTMLElement;
                const $deleteBtn = $card.querySelector('.revision-request-cancel-link') as HTMLElement;

                if (userEvent.pointNumber) {
                    $badge.classList.add('rev-badge-spot');
                    $badge.textContent = String(userEvent.pointNumber);
                    $type.textContent = 'Spot comment';
                } else {
                    // Drop badge styling — just show the icon
                    $badge.classList.remove('badge', 'rounded-circle');
                    $badge.classList.add('rev-badge-general');
                    $badge.innerHTML = chatBubbleSvg;
                    $type.textContent = 'General comment';
                }

                if (userEvent.created.isValid) {
                    const d = userEvent.created;
                    $time.textContent = `${d.toFormat('MMM d, yyyy')} \u00b7 ${d.toFormat('h:mm a')}`;
                }

                renderContent($content, userEvent);
                renderThumbnails($images, userEvent);

                $deleteBtn.addEventListener('click', () => {
                    $card.classList.add('d-none');

                    // Also hide the corresponding spot marker if it exists
                    const $marker = proofImgWrapper.querySelector(`.spot-comment[data-event-id="${userEvent.id}"]`) as HTMLElement | null;
                    $marker?.classList.add('d-none');

                    DeleteRevisionRequest(userEvent.id, quoteKey).
                        then(() => {
                            commentCount--;
                            $commentCountEl.textContent = String(commentCount);

                            toast.Undo('Deleted comment.', () => UpdateRevisionRequest(userEvent.id, { quoteKey, active: true }).then(() => {
                                commentCount++;
                                $commentCountEl.textContent = String(commentCount);
                                $card.classList.remove('d-none');
                                $marker?.classList.remove('d-none');
                            }), {
                                onExpire: () => {
                                    $card.remove();
                                    $marker?.remove();
                                },
                            });
                        }).catch(err => {
                            ShowError(err);
                            $card.classList.remove('d-none');
                            $marker?.classList.remove('d-none');
                        });
                });

                $comments.insertBefore($card, $comments.firstElementChild);
            };

            const appendSpotComment = (userEvent: models.UserEvent) => {
                const $spotComment = template.Clone($spotCommentTemplate).firstElementChild as HTMLElement;
                const $icon = $spotComment.querySelector('.spot-comment-icon') as HTMLElement;
                const $iconNew = $spotComment.querySelector('.spot-comment-icon-new') as HTMLElement;

                const pin = userEvent.proofPin();
                const pcts = imgPointToPct(pin?.x ?? 0, pin?.y ?? 0);
                $spotComment.style.left = `${pcts.x}%`;
                $spotComment.style.top = `${pcts.y}%`;

                if (!userEvent.pointNumber) {
                    userEvent.pointNumber = nextSpotNumber();
                }

                $icon.textContent = String(userEvent.pointNumber);
                $iconNew.textContent = String(userEvent.pointNumber);

                if (!userEvent.id) {
                    // New spot comment — show form
                    $spotComment.classList.add('spot-comment-new');

                    const $newFormContainer = $spotComment.querySelector('.spot-comment-new-form') as HTMLElement;
                    const $newForm = $newFormContainer.querySelector('form') as HTMLFormElement;
                    const $newFieldset = $newForm.querySelector('fieldset') as HTMLFieldSetElement;
                    const $newTextarea = $newForm.querySelector('textarea') as HTMLTextAreaElement;
                    const $newSubmit = $newForm.querySelector('button[type="submit"]') as HTMLButtonElement;
                    const $newCancel = $newForm.querySelector('.spot-comment-cancel') as HTMLButtonElement;
                    const $newAttachments = $newForm.querySelector('.spot-comment-attachments') as HTMLLabelElement;
                    const $newThumbnails = $newForm.querySelector('.spot-comment-thumbnails') as HTMLElement;

                    const popper = createPopper($iconNew, $newFormContainer, popperConfig);

                    const interactable = interact($iconNew).draggable({
                        listeners: {
                            move(event: DragEvent) {
                                pcts.x = Math.max(0, Math.min(pcts.x + (event.dx / proofImg.clientWidth) * 100, 100));
                                pcts.y = Math.max(0, Math.min(pcts.y + (event.dy / proofImg.clientHeight) * 100, 100));

                                $spotComment.style.left = `${pcts.x}%`;
                                $spotComment.style.top = `${pcts.y}%`;
                                void popper.update();
                            },
                        },
                    });

                    proofImgWrapper.classList.add('proof-col--active-new-spot');

                    // Tracks in-flight attachment uploads so submit stays disabled
                    // until the S3 PUT finishes. Without this, the thumbnail's
                    // MutationObserver below re-enables submit the moment the file is
                    // queued — letting the user submit mid-upload, which disables the
                    // fieldset and then stalls forever awaiting spotUploader.uploadIDs().
                    let uploading = false;

                    const spotUploader = new Uploader({
                        $input: $newAttachments.querySelector('input[type="file"]') as HTMLInputElement,
                        $thumbnailsParent: $newThumbnails,
                        $submitButtons: [$newSubmit],
                        onLoaderStart: () => {
                            uploading = true;
                            updateNewSubmitDisabled();
                        },
                        onLoaderStop: () => {
                            uploading = false;
                            updateNewSubmitDisabled();
                        },
                    });

                    const updateNewSubmitDisabled = () => {
                        $newSubmit.disabled = uploading || (!$newTextarea.value.trim() && !spotUploader.uploads().length);
                    };
                    updateNewSubmitDisabled();

                    $newTextarea.addEventListener('input', () => {
                        $newTextarea.style.height = 'auto';
                        $newTextarea.style.height = `${$newTextarea.scrollHeight}px`;
                        updateNewSubmitDisabled();
                    });

                    // Re-check when uploads are added/removed
                    new MutationObserver(updateNewSubmitDisabled).observe($newThumbnails, { childList: true });

                    const cleanup = () => {
                        interactable.unset();
                        popper.destroy();
                        $spotComment.remove();
                        proofImgWrapper.classList.remove('proof-col--active-new-spot');
                    };

                    $newCancel.addEventListener('click', () => cleanup());
                    $newTextarea.addEventListener('keydown', e => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            $newForm.requestSubmit();
                        } else if (e.key === 'Escape') {
                            cleanup();
                        }
                    });

                    $newForm.noValidate = true;
                    $newForm.addEventListener('submit', async e => {
                        e.preventDefault();

                        if (!$newTextarea.value.trim() && !spotUploader.uploads().length) {
                            return;
                        }

                        if (!ReportValidity($newForm)) {
                            return;
                        }

                        buttons.Start($newSubmit);
                        $newFieldset.disabled = true;

                        const point = pctToImgPoint(pcts.x, pcts.y);

                        const capturedSpotUploads = captureUploads(spotUploader);

                        // Awaited outside the chain — a PUT-failure throw would
                        // skip the catch/finally below and leave the form disabled.
                        let uploadIDs: number[];
                        try {
                            uploadIDs = await spotUploader.uploadIDs();
                        } catch (err) {
                            ShowError(err);
                            $newFieldset.disabled = false;
                            buttons.Stop($newSubmit);
                            return;
                        }

                        CreateRevisionRequests({
                            quoteKey,
                            revisionRequests: [{
                                quoteProofID: proof.id ?? '',
                                notes: $newTextarea.value,
                                uploadIDs,
                                point,
                                pointNumber: userEvent.pointNumber,
                            }],
                        }).then(created => {
                            const newUserEvent = new models.UserEvent({
                                id: created.userEventIDs[0],
                                content: $newTextarea.value,
                                contentType: 'text/plain',
                                created: DateTime.local(),
                                point,
                                pointNumber: userEvent.pointNumber,
                                userEventUploads: capturedSpotUploads,
                            });

                            // Add committed marker on the image
                            appendSpotComment(newUserEvent);

                            // Add card to sidebar
                            prependComment(newUserEvent);

                            toast.Success('Your spot comment has been submitted.');

                            cleanup();
                        }).catch(ShowError).
                            finally(() => {
                                $newFieldset.disabled = false;
                                buttons.Stop($newSubmit);
                            });
                    });

                    setTimeout(() => $newTextarea.focus());
                } else {
                    // Existing spot comment — just the marker, details are in the sidebar card
                    $spotComment.dataset.eventId = userEvent.id;

                    // Click marker to scroll to the corresponding sidebar card
                    $icon.addEventListener('click', () => {
                        const $card = $comments.querySelector(`[data-event-id="${userEvent.id}"]`) as HTMLElement | null;
                        if ($card) {
                            $card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            $card.classList.add('rev-card--highlight');
                            setTimeout(() => $card.classList.remove('rev-card--highlight'), 2000);
                        }
                    });
                }

                proofImgPositioner.appendChild($spotComment);
            };

            // Click handler on proof image to add new spot comments
            proofImg.addEventListener('click', e => {
                if (proofImgWrapper.classList.contains('proof-col--active-new-spot')) {
                    return;
                }

                const rect = proofImg.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / proofImg.clientWidth) * proofImg.naturalWidth;
                const y = ((e.clientY - rect.top) / proofImg.clientHeight) * proofImg.naturalHeight;

                appendSpotComment(new models.UserEvent({ point: { x, y } }));
            });

            // Load all existing comments in a single pass (sorted by time)
            // so general and spot comments are interleaved correctly.
            // We wait for the image to load first since spot comments need dimensions.
            const orderedUserEvents = [...proof.userEvents].sort((a, b) =>
                a.created.toMillis() - b.created.toMillis(),
            );

            const renderAllComments = () => {
                for (const userEvent of orderedUserEvents) {
                    if (userEvent.proofPin()) {
                        appendSpotComment(userEvent);
                    }
                    prependComment(userEvent);
                }
            };

            if (proofImg.complete && proofImg.naturalWidth > 0) {
                renderAllComments();
            } else {
                proofImg.addEventListener('load', renderAllComments, { once: true });
                proofImg.addEventListener('error', renderAllComments, { once: true });
            }

            // General notes form submission
            const generalNotes = form.querySelector('textarea') as HTMLTextAreaElement;

            generalNotes.addEventListener('keydown', e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    form.requestSubmit();
                }
            });

            form.addEventListener('submit', async e => {
                e.preventDefault();

                const content = generalNotes.value.trim();
                if (!content && !v2Uploader.uploads().length) {
                    return;
                }

                const userEventUploads = captureUploads(v2Uploader);

                buttons.Start(submit);

                // Awaited outside the chain — a PUT-failure throw would skip
                // the catch/finally below and leave the button spinning.
                let uploadIDs: number[];
                try {
                    uploadIDs = await v2Uploader.uploadIDs();
                } catch (err) {
                    ShowError(err);
                    buttons.Stop(submit);
                    return;
                }

                CreateRevisionRequests({
                    quoteKey,
                    revisionRequests: [{
                        quoteProofID: proof.id ?? '',
                        notes: content,
                        uploadIDs,
                    }],
                }).
                    then(created => {
                        toast.Success('Revision request submitted!');

                        prependComment(new models.UserEvent({
                            id: created.userEventIDs[0],
                            content,
                            contentType: 'text/plain',
                            created: DateTime.local(),
                            userEventUploads,
                        }));

                        generalNotes.value = '';
                        v2Uploader.reset();
                    }).
                    catch(ShowError).
                    finally(() => {
                        buttons.Stop(submit);
                    });
            });
        }
    } else {
        hasProofsContainer.classList.add('d-none');
        noProofsContainer.classList.remove('d-none');

        const noProofsRevisionsForm = document.querySelector('#no-proofs-revisions-form') as HTMLFormElement;
        const serialHeader = document.querySelector('#quote-number') as HTMLElement;
        const fullName = document.querySelector('#contact-full-name') as HTMLInputElement;
        const email = document.querySelector('#contact-email') as HTMLInputElement;
        const phone = document.querySelector('#contact-phone') as HTMLInputElement;
        const message = document.querySelector('#contact-message') as HTMLTextAreaElement;
        const uploadsLabel = document.querySelector('#uploads') as HTMLLabelElement;
        const submitBtn = document.querySelector('#contact-submit') as HTMLButtonElement;
        const requestSuccess = document.querySelector('#revision-success') as HTMLElement;

        serialHeader.textContent = serial;

        const ph = new Phone(phone);

        await sess.then(sess => {
            if (sess) {
                fullName.value = sess.user.fullName();
                email.value = sess.user.emailAddress.email;
                void ph.ready().then(() =>
                    ph.iti.setNumber(sess.user.phoneNumber.phone));
            }
        });

        const u = new uploads.Uploader(uploadsLabel, {
            confirmBtn: submitBtn,
            thumbnails: document.querySelector('#uploads-thumbnails') as HTMLElement,
        });

        noProofsRevisionsForm.noValidate = true;
        noProofsRevisionsForm.addEventListener('submit', e => {
            e.preventDefault();
            if (!ReportValidity(noProofsRevisionsForm)) {
                return false;
            }

            buttons.Start(submitBtn);

            CreateContact({
                name: fullName.value,
                company: '',
                phone: ph,
                email: email.value,
                content: `Revision request for quote #${serial},

                        Customer Message:
                        ${message.value},`,
                uploader: u,
                $reCaptchaPlaceholder: document.querySelector('.contact-recpatcha-parent') as HTMLElement,
            }).then(() => {
                buttons.Stop(submitBtn);
                noProofsContainer.classList.add('d-none');
                requestSuccess.classList.remove('d-none');
                toast.Success('Request Sent!');
            }).catch(err => {
                toast.Error('Something went wrong!');
                throw err;
            });
        });
    }

    spinner.parentElement?.removeChild(spinner);
}
